import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Image, { type ImageProps, type StaticImageData } from 'next/image';
import { useEffect, useRef } from 'react';
import HLSVideo from '@/components/HLSVideo.js';
import usePrefersReducedMotion from '../usePrefersReducedMotion';
import VideoTrigger, { type VideoTriggerReveal } from '../VideoTrigger/VideoTrigger';
import styles from './Media.module.scss';

/**
 * The frame's shape: `hero` 309:390 on a phone and 996:620 from 768px,
 * `landscape` 16:10, `portrait` 4:5, `square` 1:1.
 */
export type MediaRatio = 'hero' | 'landscape' | 'portrait' | 'square';
/** Corner radius: `medium` the frame radius (12px), `hero` 16px, 32px from 768px. */
export type MediaRadius = 'none' | 'medium' | 'hero';
export type MediaFit = 'cover' | 'contain';
/** `subject-right` keeps a subject on the right in view from 768px. */
export type MediaFocalPoint = 'center' | 'subject-right';
interface MediaFrameProps {
  /** Defaults to `landscape`. */
  ratio?: MediaRatio;
  /** Defaults to `medium`. */
  radius?: MediaRadius;
  /** Defaults to `cover`. */
  fit?: MediaFit;
  /** Defaults to `center`. */
  focalPoint?: MediaFocalPoint;
}

/**
 * Opening a dialog from the media (e.g. the full video in a VideoModal):
 * `onRequestModal` with the control's name, `modalLabel`. An image becomes
 * the button, named `modalLabel`; a video gets a VideoTrigger over it, the
 * translucent pill shown while the frame is hovered or holds focus.
 */
type MediaModalTrigger = {
  onRequestModal: () => void;
  modalLabel: string;
  /**
   * A video's trigger: `frame` (the default) shows it while the frame is
   * hovered or holds focus; `always` shows it at rest.
   */
  modalReveal?: VideoTriggerReveal;
} | {
  onRequestModal?: never;
  modalLabel?: never;
  modalReveal?: never;
};
export type MediaImageProps = MediaFrameProps & MediaModalTrigger & {
  kind: 'image';
  src: ImageProps['src'];
  /** The image below 768px. */
  mobileSrc?: StaticImageData | string;
  alt: string;
  /** Required, as on every next/image (root AGENTS.md, images). */
  sizes: string;
  /** Only for the page's LCP image. */
  priority?: boolean;
};
export type MediaVideoProps = MediaFrameProps & MediaModalTrigger & {
  kind: 'video';
  /** A video file or an HLS manifest; it plays muted, as decoration. */
  src: string;
  poster?: string;
  /**
   * Plays on load, unless the visitor prefers reduced motion (then it stays
   * paused on its poster: there is no play/pause control, O299 B).
   */
  autoPlay?: boolean;
  loop?: boolean;
};
export type MediaProps = MediaImageProps | MediaVideoProps;
function staticSrc(src: StaticImageData | string) {
  return typeof src === 'string' ? src : src.src;
}

/**
 * A framed image or video (Phase 4 PR 10; the RedesignMedia donor, on
 * next/image). Phase 5 adopted it on the landers and Sacramento's home
 * (the legacy components/Image.js wrapper is retired, 5.10).
 * Presentational: no analytics, no className, no style.
 */
function Media(props) {
  const $ = _c(29);
  const {
    fit: t0,
    focalPoint: t1,
    ratio: t2,
    radius: t3
  } = props;
  const fit = t0 === undefined ? "cover" : t0;
  const focalPoint = t1 === undefined ? "center" : t1;
  const ratio = t2 === undefined ? "landscape" : t2;
  const radius = t3 === undefined ? "medium" : t3;
  const frameRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isVideo = props.kind === "video";
  const autoPlay = isVideo && props.autoPlay === true;
  const videoSrc = isVideo ? props.src : undefined;
  const frame = classNames(styles["helios-media"], styles[`helios-media--ratio-${ratio}`], styles[`helios-media--radius-${radius}`], styles[`helios-media--fit-${fit}`], styles[`helios-media--focal-${focalPoint}`]);
  let t4;
  let t5;
  if ($[0] !== autoPlay || $[1] !== prefersReducedMotion || $[2] !== videoSrc) {
    t4 = () => {
      const video = frameRef.current?.querySelector("video");
      if (!videoSrc || !video) {
        return;
      }
      if (prefersReducedMotion) {
        video.pause();
        return;
      }
      if (!autoPlay) {
        return;
      }
      const play = () => void video.play()?.catch(_temp);
      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        play();
        return;
      }
      video.addEventListener("canplay", play, {
        once: true
      });
      return () => video.removeEventListener("canplay", play);
    };
    t5 = [autoPlay, prefersReducedMotion, videoSrc];
    $[0] = autoPlay;
    $[1] = prefersReducedMotion;
    $[2] = videoSrc;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  if (props.kind === "image") {
    let t6;
    if ($[5] !== props.alt || $[6] !== props.priority || $[7] !== props.sizes || $[8] !== props.src) {
      t6 = <Image alt={props.alt} className={styles["helios-media__image"]} fill={true} priority={props.priority} sizes={props.sizes} src={props.src} />;
      $[5] = props.alt;
      $[6] = props.priority;
      $[7] = props.sizes;
      $[8] = props.src;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const image = t6;
    let t7;
    if ($[10] !== image || $[11] !== props.mobileSrc) {
      t7 = props.mobileSrc ? <picture className={styles["helios-media__picture"]}><source media="(max-width: 767px)" srcSet={staticSrc(props.mobileSrc)} />{image}</picture> : image;
      $[10] = image;
      $[11] = props.mobileSrc;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const content = t7;
    if (props.onRequestModal) {
      return <button aria-label={props.modalLabel} className={classNames(frame, styles["helios-media--trigger"])} data-helios-media="image" onClick={props.onRequestModal} type="button">{content}</button>;
    }
    let t8;
    if ($[13] !== content || $[14] !== frame) {
      t8 = <div className={frame} data-helios-media="image">{content}</div>;
      $[13] = content;
      $[14] = frame;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  const t6 = autoPlay && !prefersReducedMotion;
  let t7;
  if ($[16] !== props.loop || $[17] !== props.poster || $[18] !== props.src || $[19] !== t6) {
    t7 = <HLSVideo aria-hidden="true" autoPlay={t6} className={styles["helios-media__video"]} loop={props.loop} muted={true} playsInline={true} poster={props.poster} preload="metadata" src={props.src} />;
    $[16] = props.loop;
    $[17] = props.poster;
    $[18] = props.src;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== props.modalLabel || $[22] !== props.modalReveal || $[23] !== props.onRequestModal) {
    t8 = props.onRequestModal ? <div className={styles["helios-media__modal-trigger"]}><VideoTrigger hook="media-trigger" onClick={props.onRequestModal} reveal={props.modalReveal}>{props.modalLabel}</VideoTrigger></div> : null;
    $[21] = props.modalLabel;
    $[22] = props.modalReveal;
    $[23] = props.onRequestModal;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== frame || $[26] !== t7 || $[27] !== t8) {
    t9 = <div className={frame} data-helios-media="video" data-helios-video-frame="" ref={frameRef}>{t7}{t8}</div>;
    $[25] = frame;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function _temp() {}
export default Media;